if (!customElements.get('product-form')) {
	customElements.define('product-form', class ProductForm extends HTMLElement {
		constructor() {
			super();

			this.form = this.querySelector('form');
			this.form.querySelector('[name=id]').disabled = false;
			this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
			this.cart = document.querySelector('cart-drawer');
			this.submitButton = this.querySelector('[type="submit"]');
			if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

			this.hideErrors = this.dataset.hideErrors === 'true';
		}

		getCartSectionIds() {
			return this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
		}

		appendCartSections(target) {
			const sections = this.getCartSectionIds();
			if (!sections.length) return;

			if (target instanceof FormData) {
				target.append('sections', sections.join(','));
				target.append('sections_url', window.location.pathname);
				return;
			}

			target.sections = sections.join(',');
			target.sections_url = window.location.pathname;
		}

		hasCartSections(parsedState) {
			const sections = this.getCartSectionIds();
			return parsedState?.sections && sections.every((sectionId) => parsedState.sections[sectionId]);
		}

		ensureCartSections(parsedState) {
			if (!this.cart || this.hasCartSections(parsedState)) return Promise.resolve(parsedState);

			const sections = this.getCartSectionIds();
			if (!sections.length) return Promise.resolve(parsedState);

			const params = new URLSearchParams({
				sections: sections.join(','),
				sections_url: window.location.pathname,
			});
			const cartUrl = window.routes?.cart_url || '/cart';

			return fetch(`${cartUrl}?${params.toString()}`, {
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
			})
				.then((response) => response.json())
				.then((sectionHtml) => ({
					...parsedState,
					sections: sectionHtml,
				}));
		}

		renderCartContents(parsedState) {
			return this.ensureCartSections(parsedState).then((state) => {
				if (!this.hasCartSections(state)) {
					window.location = window.routes?.cart_url || '/cart';
					return;
				}

				const quickAddModal = this.closest('quick-add-modal');
				if (quickAddModal) {
					document.body.addEventListener('modalClosed', () => {
						setTimeout(() => { this.cart.renderContents(state); });
					}, { once: true });
					quickAddModal.hide(true);
				} else {
					this.cart.renderContents(state);
				}
			});
		}

		getPartType(partsMap, variantId) {
			return partsMap[variantId]?.toString().toLowerCase();
		}

		getSideOptionIndex(variantData, partsMap, currentVariant, partType) {
			const directSideIndex = currentVariant.options.findIndex((option) => {
				const normalizedOption = option?.toString().toLowerCase();
				return normalizedOption === 'left' || normalizedOption === 'right' || normalizedOption === 'pair';
			});

			if (directSideIndex >= 0) return directSideIndex;

			const currentPartType = partType || this.getPartType(partsMap, currentVariant.id);
			const oppositePartType = currentPartType === 'left' ? 'right' : 'left';

			const sibling = variantData.find((variant) => {
				if (this.getPartType(partsMap, variant.id) !== oppositePartType) return false;

				const mismatchIndexes = currentVariant.options.reduce((indexes, option, index) => {
					if (variant.options[index] !== option) indexes.push(index);
					return indexes;
				}, []);

				return mismatchIndexes.length === 1;
			});

			if (!sibling) return -1;

			return currentVariant.options.findIndex((option, index) => sibling.options[index] !== option);
		}

		findPairVariant(variantData, partsMap, currentOptions, sideOptionIndex, targetPartType) {
			return variantData.find((variant) => {
				if (!variant.available) return false;
				if (this.getPartType(partsMap, variant.id) !== targetPartType) return false;

				return variant.options.every((option, index) => {
					return index === sideOptionIndex || option === currentOptions[index];
				});
			});
		}

		getPickerSelectedOptions(picker) {
			const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
			const selectedOptions = fieldsets.map((fieldset) => {
				return Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((radio) => radio.checked)?.value;
			});

			return selectedOptions.every(Boolean) ? selectedOptions : null;
		}

		getPickerSideOptionIndex(picker) {
			return Array.from(picker.querySelectorAll('fieldset')).findIndex((fieldset) => {
				return fieldset.querySelector('[data-custom-pair-button]');
			});
		}

		normalizePairName(value) {
			return value?.toString().trim().replace(/\s*\/\s*/g, ' / ').replace(/\s+/g, ' ').toLowerCase();
		}

		getPairMetaEntry(picker, pairButton) {
			if (!picker || !pairButton?.dataset.pairVariants) return null;

			let pairVariants = [];
			try {
				pairVariants = JSON.parse(pairButton.dataset.pairVariants);
			} catch (error) {
				console.error('[Pair Variants]', error);
				return null;
			}

			const variantData = picker.variantData || JSON.parse(picker.querySelector('[type="application/json"]').textContent);
			const currentVariantId = this.form.querySelector('[name="id"]')?.value;
			const currentVariant = picker.currentVariant || variantData.find(v => v.id.toString() === currentVariantId?.toString());
			const currentOptions = this.getPickerSelectedOptions(picker) || currentVariant?.options;
			if (!currentOptions) return null;

			let sideOptionIndex = this.getPickerSideOptionIndex(picker);
			if (sideOptionIndex < 0 && currentVariant) {
				sideOptionIndex = this.getSideOptionIndex(variantData, {}, currentVariant);
			}
			if (sideOptionIndex < 0) return null;

			const pairOptions = [...currentOptions];
			pairOptions[sideOptionIndex] = 'Pair';
			const pairVariantName = this.normalizePairName(pairOptions.join(' / '));

			return pairVariants.find((entry) => {
				return this.normalizePairName(entry.variant_name) === pairVariantName;
			}) || null;
		}

		getPairVariants(picker, partsMap) {
			if (!picker) return null;

			const variantData = picker.variantData || JSON.parse(picker.querySelector('[type="application/json"]').textContent);
			if (!picker.currentVariant) {
				const currentVariantId = this.form.querySelector('[name="id"]')?.value;
				picker.currentVariant = variantData.find(v => v.id.toString() === currentVariantId?.toString());
			}

			const currentOptions = this.getPickerSelectedOptions(picker) || picker.currentVariant?.options;
			if (!currentOptions) return null;

			let sideOptionIndex = this.getPickerSideOptionIndex(picker);
			if (sideOptionIndex < 0 && picker.currentVariant) {
				const currentPartType = this.getPartType(partsMap, picker.currentVariant.id);
				sideOptionIndex = this.getSideOptionIndex(variantData, partsMap, picker.currentVariant, currentPartType);
			}
			if (sideOptionIndex < 0) return null;

			const leftV = this.findPairVariant(variantData, partsMap, currentOptions, sideOptionIndex, 'left');
			const rightV = this.findPairVariant(variantData, partsMap, currentOptions, sideOptionIndex, 'right');

			if (!leftV || !rightV) return null;

			return { variantData, leftV, rightV, sideOptionIndex };
		}

		getPairId(picker, partsMap, variantData, currentVariant, partType, sideOptionIndex = -1) {
			const productHandle = picker.dataset.url.split('?')[0].split('/').pop();
			const currentOptions = currentVariant.options;

			let sharedOptions = currentOptions;
			let sideIndex = sideOptionIndex;

			if (sideIndex < 0) {
				sideIndex = this.getSideOptionIndex(variantData, partsMap, currentVariant, partType);
			}

			if (sideIndex >= 0) {
				sharedOptions = currentOptions.filter((opt, index) => index !== sideIndex);
			}

			const optionsString = sharedOptions.join('-').toLowerCase().replace(/[^a-z0-9а-яіїєґ]+/g, '-');
			return optionsString ? `${productHandle}-${optionsString}` : productHandle;
		}

		onSubmitHandler(evt) {
			evt.preventDefault();
			if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

			const formId = this.form.getAttribute('id');
			const sectionId = formId ? formId.replace('product-form-', '') : '';
			const picker = document.getElementById(`variant-radios-${sectionId}`) || document.getElementById(`variant-selects-${sectionId}`);

			let partsMap = {};
			let partType = null;

			if (this.customPartsData) {
				partsMap = this.customPartsData.map;
				partType = this.customPartsData.type;
			} else if (picker) {
				const partsMapEl = picker.closest('.product-parameters')?.querySelector('[data-variant-parts]');
				if (partsMapEl) {
					try {
						partsMap = JSON.parse(partsMapEl.textContent);
						if (!picker.currentVariant) {
							const currentVariantId = this.form.querySelector('[name="id"]')?.value;
							const variantData = picker.variantData || JSON.parse(picker.querySelector('[type="application/json"]').textContent);
							picker.currentVariant = variantData.find(v => v.id.toString() === currentVariantId?.toString());
						}
						partType = partsMap[picker.currentVariant?.id];
					} catch (e) {
						console.error("Error parsing JSON metafields", e);
					}
				}
			}

			if (this.form.dataset.customPairActive === 'true') {
				partType = 'pair';
			}

			if (partType === 'pair') {
				this.handlePairAdd(picker, partsMap, partType);
				return;
			}

			this.handleErrorMessage();
			this.submitButton.setAttribute('aria-disabled', true);
			this.submitButton.classList.add('loading');
			this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

			const config = fetchConfig('javascript');
			config.headers['X-Requested-With'] = 'XMLHttpRequest';
			delete config.headers['Content-Type'];

			const formData = new FormData(this.form);

			if (picker && picker.currentVariant && (partType === 'left' || partType === 'right')) {
				const variantData = picker.variantData || JSON.parse(picker.querySelector('[type="application/json"]').textContent);
				const pairId = this.getPairId(picker, partsMap, variantData, picker.currentVariant, partType);

				const sideName = partType === 'left' ? 'Left' : 'Right';

				formData.append('properties[_half_product]', `${sideName}|${pairId}`);
			}

			if (this.cart) {
				this.appendCartSections(formData);
				this.cart.setActiveElement(document.activeElement);
			}
			config.body = formData;

			fetch(`${routes.cart_add_url}`, config)
				.then(async r => {
					const text = await r.text();
					try {
						return JSON.parse(text);
					} catch (err) {
						console.warn('[Single Add]');
						return { status: 200 };
					}
				})
				.then((response) => {
					if (response.status && response.status !== 200) {
						if(window.publish) window.publish(PUB_SUB_EVENTS?.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
						this.handleErrorMessage(response.description);
						const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
						if (!soldOutMessage) return;
						this.submitButton.setAttribute('aria-disabled', true);
						this.submitButton.querySelector('span').classList.add('hidden');
						soldOutMessage.classList.remove('hidden');
						this.error = true;
						return;
					} else if (!this.cart) {
						window.location = window.routes.cart_url;
						return;
					}

					if (!this.error && window.publish) window.publish(PUB_SUB_EVENTS?.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
					this.error = false;
					return this.renderCartContents(response);
				})
				.catch((e) => {
					console.error(e);
				})
				.finally(() => {
					this.submitButton.classList.remove('loading');
					if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
					if (!this.error) this.submitButton.removeAttribute('aria-disabled');
					this.querySelector('.loading-overlay__spinner').classList.add('hidden');
				});
		}
		handlePairAdd(picker, partsMap, partType) {
			if (!picker) {
				this.handleErrorMessage(window.variantStrings?.unavailable || "Pair options unavailable");
				return;
			}

			const pairVariants = this.getPairVariants(picker, partsMap);
			if (!pairVariants) {
				this.handleErrorMessage(window.variantStrings?.unavailable || "Pair options unavailable");
				return;
			}

			const { variantData, leftV, rightV, sideOptionIndex } = pairVariants;
			const pairId = this.getPairId(picker, partsMap, variantData, leftV, 'left', sideOptionIndex);

			const formId = this.form.getAttribute('id');
			const qtyInput = this.form.elements['quantity'] || document.querySelector(`[name="quantity"][form="${formId}"]`);
			const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;

			this.handleErrorMessage();
			this.submitButton.setAttribute('aria-disabled', 'true');
			this.submitButton.classList.add('loading');
			this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

			if (this.cart) this.cart.setActiveElement(document.activeElement);

			const body = {
				items: [
					{
						id: leftV.id,
						quantity: quantity,
						properties: { '_half_product': `Left|${pairId}` }
					},
					{
						id: rightV.id,
						quantity: quantity,
						properties: { '_half_product': `Right|${pairId}` }
					}
				]
			};

			this.appendCartSections(body);

			fetch(window.routes?.cart_add_url || '/cart/add.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
				body: JSON.stringify(body)
			})
				.then(async r => {
					const text = await r.text();
					try {
						return JSON.parse(text);
					} catch (err) {
						console.warn('[Pair]');
						return { status: 200 };
					}
				})
				.then(data => {
					if (data.status && data.status !== 200) {
						this.handleErrorMessage(data.description);
						return;
					}

					if (!this.cart) {
						window.location = window.routes?.cart_url || '/cart';
						return;
					}

					return this.renderCartContents(data);
				})
				.catch(e => console.error('[Pair Error]', e))
				.finally(() => {
					this.submitButton.classList.remove('loading');
					if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
					this.submitButton.removeAttribute('aria-disabled');
					this.querySelector('.loading-overlay__spinner').classList.add('hidden');
				});
		}

		handleErrorMessage(errorMessage = false) {
			if (this.hideErrors) return;

			this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
			if (!this.errorMessageWrapper) return;
			this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

			this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

			if (errorMessage) {
				this.errorMessage.textContent = errorMessage;
			}
		}
	});

	document.addEventListener('click', (event) => {
		const pairButton = event.target.closest('[data-custom-pair-button]');
		if (!pairButton) return;

		event.preventDefault();
		event.stopPropagation();

		const form = document.getElementById(pairButton.dataset.formId) || document.querySelector('.product-form form');
		const productForm = form?.closest('product-form');
		const formId = form?.getAttribute('id') || '';
		const sectionId = formId.replace('product-form-', '');
		const picker = document.getElementById(`variant-radios-${sectionId}`) || document.getElementById(`variant-selects-${sectionId}`);
		const partsMapEl = picker?.closest('.product-parameters')?.querySelector('[data-variant-parts]');
		const submitButton = productForm?.querySelector('[type="submit"]');
		let partsMap = {};

		if (partsMapEl) {
			try {
				partsMap = JSON.parse(partsMapEl.textContent);
			} catch (error) {
				console.error("Error parsing JSON metafields", error);
			}
		}

		const pairVariants = productForm?.getPairVariants?.(picker, partsMap);
		const pairMetaEntry = productForm?.getPairMetaEntry?.(picker, pairButton);
		if (!pairVariants || !pairMetaEntry) {
			if (form) form.dataset.customPairActive = 'false';
			pairButton.classList.remove('is-active');
			pairButton.classList.add('is-unavailable');
			pairButton.removeAttribute('disabled');
			pairButton.setAttribute('aria-disabled', 'false');
			pairButton.setAttribute('aria-pressed', 'false');
			if (submitButton) {
				submitButton.setAttribute('disabled', 'disabled');
				submitButton.setAttribute('aria-disabled', 'true');
				const submitButtonText = submitButton.querySelector('span');
				if (submitButtonText) submitButtonText.textContent = window.variantStrings?.soldOut || "Sold out";
			}
			return;
		}

		pairButton.dataset.activePairVariantName = pairMetaEntry.variant_name || '';

		const { leftV, rightV } = pairVariants;
		const totalCents = (leftV?.price || 0) + (rightV?.price || 0);
		const discountPct = window.theme?.pairDiscount?.percentage || 0;
		const discountedCents = Math.round(totalCents * (1 - discountPct / 100));
		const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
		const formattedPrice = moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, (discountedCents / 100).toFixed(2));
		pairButton.dataset.activePairPrice = formattedPrice;

		pairButton.dataset.activePairImages = JSON.stringify(pairMetaEntry.gallery_images || []);

		const fieldset = pairButton.closest('fieldset');
		if (fieldset) {
			fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
				input.checked = false;
			});
		}

		if (form) form.dataset.customPairActive = 'true';
		pairButton.classList.add('is-active');
		pairButton.classList.remove('is-unavailable');
		pairButton.removeAttribute('disabled');
		pairButton.setAttribute('aria-disabled', 'false');
		pairButton.setAttribute('aria-pressed', 'true');

		if (submitButton) {
			submitButton.removeAttribute('disabled');
			submitButton.removeAttribute('aria-disabled');
			const submitButtonText = submitButton.querySelector('span');
			if (submitButtonText) submitButtonText.textContent = window.variantStrings?.addToCart || "Add to cart";
		}

		if (picker?.updateCustomPairButtonState) picker.updateCustomPairButtonState();
	}, true);

	document.addEventListener('change', (event) => {
		if (event.target.tagName !== 'INPUT' || event.target.type !== 'radio') return;

		const fieldset = event.target.closest('fieldset');
		const pairButton = fieldset?.querySelector('[data-custom-pair-button]');
		if (!pairButton) return;

		const form = document.getElementById(pairButton.dataset.formId) || document.querySelector('.product-form form');
		if (form) form.dataset.customPairActive = 'false';
		pairButton.classList.remove('is-active', 'is-unavailable');
		pairButton.setAttribute('aria-disabled', 'false');
		pairButton.setAttribute('aria-pressed', 'false');
	}, true);
}